import {DataStatus, Flag, Separator, showNotification} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {ServiceAccountResponseBody, useUpdateServiceAccountMutation} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {getServiceAccountStatusFlagProps} from '../utils/getServiceAccountStatusFlagProps';
import {ServiceAccountForm, ServiceAccountFormValues} from './ServiceAccountForm';

interface ServiceAccountGeneralTabProps extends RequiredTestIdProps {
  serviceAccount: ServiceAccountResponseBody;
}

export function ServiceAccountGeneralTab(props: ServiceAccountGeneralTabProps) {
  const navigate = useNavigate();
  const {data: user, isLoading, isError} = useGetUserQuery({id: props.serviceAccount.userId});
  const [updateServiceAccount] = useUpdateServiceAccountMutation();

  const handleSubmit = (values: ServiceAccountFormValues) =>
    updateServiceAccount({
      serviceAccountId: props.serviceAccount.id,
      updateServiceAccountRequestBody: {
        name: values.name,
        description: values.description || null,
        roles: values.roles,
        branches: values.branches,
      },
    })
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  return (
    <VStack spacing={6}>
      <HStack>
        <Flag
          {...getServiceAccountStatusFlagProps(props.serviceAccount.status)}
          data-testid={suffixTestId('status', props)}
        />
      </HStack>
      <Separator spacing={0} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        {isNotNil(user) ? (
          <ServiceAccountForm
            mode="edit"
            defaultValues={{
              name: props.serviceAccount.name,
              description: props.serviceAccount.description ?? '',
              email: user.email,
              maxTokenLifetime: String(props.serviceAccount.maxTokenLifetime),
              roles: user.roles?.map((role) => role.id) ?? [],
              branches: user.branches?.map((branch) => branch.id) ?? [],
            }}
            submitButtonTitle={i18n.t('general.actions.save')}
            onSubmit={handleSubmit}
            onCancel={() => navigate(settingsRoutes.serviceAccountManagement)}
            data-testid={suffixTestId('form', props)}
          />
        ) : null}
      </DataStatus>
    </VStack>
  );
}
